<script setup lang="ts">
import { RouterLink } from 'vue-router';

import BrandLogo from '#/components/layout/BrandLogo.vue';
import { brand } from '#/config/brand';

/**
 * 전역 푸터(AppFooter) — 워크스페이스 셸 공용.
 * 브랜드 + 워크스페이스 링크 열 + 사업자 정보. 2열(lg 980) → 1열(md 600) 그리드.
 */

// ⚠️ 프리렌더 대상(공개·색인) 경로는 trailing slash 정규형으로 링크한다 — 크롤러가 푸터 링크를
// 전 페이지에서 따라가므로, 슬래시가 빠지면 301 을 한 번씩 태운다(근거: brand.ts canonicalUrl).
const workspaceLinks = [
  { label: '정시 분석', to: '/jeongsi' },
  { label: '수시 분석', to: '/susi' },
  // 진로심리검사는 노출 보류(data/analysis/visibility.ts) — 되살릴 때 링크도 함께 복귀.
] as const;

const legalLinks = [
  { label: '이용약관', to: '/legal/terms/' },
  { label: '개인정보처리방침', to: '/legal/privacy/' },
  { label: '사업자정보', to: '/legal/business/' },
] as const;

const colLinkClass = 'text-sm font-medium transition-colors hover:text-white';
</script>

<template>
  <footer class="bg-footer-bg text-footer-text">
    <div class="mx-auto max-w-page px-[18px] pb-[26px] pt-11 md:px-8 md:pt-[54px] xl:px-10">
      <!-- 상단: 브랜드 + 워크스페이스 링크 -->
      <div
        class="grid grid-cols-1 gap-7 border-b border-white/[0.08] pb-8 md:grid-cols-2 md:gap-10"
      >
        <!-- 브랜드 -->
        <div>
          <div class="mb-4">
            <BrandLogo variant="invert" />
          </div>
          <p class="m-0 max-w-[360px] text-[13.5px] leading-[1.7] text-footer-text-dim">
            <span class="whitespace-nowrap">공개된 입시 데이터를</span>{{ ' ' }}
            <span class="whitespace-nowrap">학과·전형 단위로 해석해,</span>{{ ' ' }}
            <span class="whitespace-nowrap">학생이 직접 조건을 바꿔가며</span>{{ ' ' }}
            <span class="whitespace-nowrap">지원 전략을 설계하는</span>{{ ' ' }}
            <span class="whitespace-nowrap">AI 입시 설계 서비스입니다.</span>
          </p>
          <!--
            SNS — 회색 박스 + blog 말풍선, hover 시 네이버 공식 지정색(--naver)으로 전환.
            "blog" 글자는 mask 로 뚫어 박스 배경색이 상태와 무관하게 그대로 비친다.
          -->
          <div v-if="brand.naverBlogUrl" class="mt-5">
            <a
              :href="brand.naverBlogUrl"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="네이버 블로그 바로가기"
              class="group inline-flex h-10 w-10 items-center justify-center rounded-xl bg-footer-label transition-colors duration-150 hover:bg-naver"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" class="h-9 w-9">
                <mask id="naver-blog-glyph">
                  <path
                    fill="white"
                    d="M5.5 5h13a3 3 0 0 1 3 3v5.5a3 3 0 0 1-3 3h-5.2l-1.6 2.4-1.6-2.4H5.5a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3Z"
                  />
                  <text
                    x="12"
                    y="10.75"
                    text-anchor="middle"
                    dominant-baseline="middle"
                    font-family="ui-sans-serif, system-ui, sans-serif"
                    font-size="7"
                    font-weight="800"
                    fill="black"
                  >
                    blog
                  </text>
                </mask>
                <rect
                  width="24"
                  height="24"
                  mask="url(#naver-blog-glyph)"
                  class="fill-footer-bg transition-colors duration-150 group-hover:fill-white"
                />
              </svg>
            </a>
          </div>
        </div>

        <!-- AI 입시분석 -->
        <div>
          <h5 class="m-0 mb-3.5 text-xs font-extrabold tracking-[0.06em] text-footer-label">
            AI 입시분석
          </h5>
          <ul class="m-0 list-none p-0">
            <li v-for="link in workspaceLinks" :key="link.label" class="mb-2.5">
              <RouterLink :to="link.to" :class="colLinkClass">{{ link.label }}</RouterLink>
            </li>
          </ul>
        </div>
      </div>

      <!-- 사업자 정보 -->
      <div class="pt-6 text-[12.5px] leading-[1.9] text-footer-text-muted">
        <p class="m-0">
          <b class="text-footer-text">상호명</b> {{ brand.legal.company }}
          &nbsp;|&nbsp;
          <b class="text-footer-text">대표자</b> {{ brand.legal.ceo }}
          &nbsp;|&nbsp;
          <b class="text-footer-text">사업자등록번호</b> {{ brand.legal.bizNo }}<br />
          <b class="text-footer-text">통신판매업신고번호</b>
          {{ brand.legal.mailOrderNo }}<br />
          <b class="text-footer-text">주소</b> {{ brand.legal.address }}
          <!-- 대표전화는 미개통 시 값이 비므로 구분자째 감춘다(brand.legal.tel 주석 참조). -->
          <template v-if="brand.legal.tel">
            &nbsp;|&nbsp;
            <b class="text-footer-text">TEL</b> {{ brand.legal.tel }}
          </template>
          &nbsp;|&nbsp;
          <b class="text-footer-text">이메일</b> {{ brand.legal.email }}
        </p>
        <div class="mt-3.5 flex flex-wrap items-center gap-x-2 gap-y-1">
          <template v-for="(link, i) in legalLinks" :key="link.to">
            <RouterLink
              :to="link.to"
              class="font-semibold text-footer-text transition-colors hover:text-white"
            >
              {{ link.label }}
            </RouterLink>
            <span v-if="i < legalLinks.length - 1" aria-hidden="true">·</span>
          </template>
        </div>
        <div class="mt-4 text-xs text-footer-text-faint">
          {{ brand.legal.copyright }}
        </div>
      </div>
    </div>
  </footer>
</template>
